import {
  createSlice,
  createAsyncThunk,
  isPending,
  isFulfilled,
  isRejected,
} from "@reduxjs/toolkit";
import http from "../../utils/axios";

// Fetch all subscription packages
export const fetchSubscriptionPackages = createAsyncThunk(
  "subscriptionPackages/fetchSubscriptionPackages",
  async ({ page = 1, per_page = 15, status, search } = {}) => {
    const params = new URLSearchParams();
    params.append("page", page);
    params.append("per_page", per_page);
    if (status !== undefined) params.append("status", status);
    if (search) params.append("search", search);
    
    const res = await http.get(`/dashboard/subscription-packages?${params.toString()}`);
    return res;
  }
);

// Create subscription package
export const createSubscriptionPackage = createAsyncThunk(
  "subscriptionPackages/createSubscriptionPackage",
  async (payload, { rejectWithValue }) => {
    try {
      const res = await http.post("/dashboard/subscription-packages", payload);
      return res;
    } catch (err) {
      return rejectWithValue(
        err.response?.data || "Failed to create subscription package"
      );
    }
  }
);

// Fetch subscription package by UUID
export const fetchSubscriptionPackageById = createAsyncThunk(
  "subscriptionPackages/fetchSubscriptionPackageById",
  async (uuid, { rejectWithValue }) => {
    try {
      const res = await http.get(`/dashboard/subscription-packages/${uuid}`);
      return res;
    } catch (err) {
      return rejectWithValue(
        err.response?.data || "Failed to fetch subscription package details"
      );
    }
  }
);

// Update subscription package
export const updateSubscriptionPackage = createAsyncThunk(
  "subscriptionPackages/updateSubscriptionPackage",
  async ({ uuid, data }, { rejectWithValue }) => {
    try {
      const res = await http.put(`/dashboard/subscription-packages/${uuid}`, data);
      return res;
    } catch (err) {
      return rejectWithValue(
        err.response?.data || "Failed to update subscription package"
      );
    }
  }
);

// Delete subscription package
export const deleteSubscriptionPackage = createAsyncThunk(
  "subscriptionPackages/deleteSubscriptionPackage",
  async (uuid, { rejectWithValue }) => {
    try {
      const res = await http.delete(`/dashboard/subscription-packages/${uuid}`);
      return { uuid, data: res };
    } catch (err) {
      return rejectWithValue(
        err.response?.data || "Failed to delete subscription package"
      );
    }
  }
);

// Enable subscription package
export const enableSubscriptionPackage = createAsyncThunk(
  "subscriptionPackages/enableSubscriptionPackage",
  async (uuid, { rejectWithValue }) => {
    try {
      const res = await http.patch(`/dashboard/subscription-packages/${uuid}/enable`);
      return res;
    } catch (err) {
      return rejectWithValue(
        err.response?.data || "Failed to enable subscription package"
      );
    }
  }
);

// Disable subscription package
export const disableSubscriptionPackage = createAsyncThunk(
  "subscriptionPackages/disableSubscriptionPackage",
  async (uuid, { rejectWithValue }) => {
    try {
      const res = await http.patch(`/dashboard/subscription-packages/${uuid}/disable`);
      return res;
    } catch (err) {
      return rejectWithValue(
        err.response?.data || "Failed to disable subscription package"
      );
    }
  }
);

// Toggle subscription package status
export const toggleSubscriptionPackageStatus = createAsyncThunk(
  "subscriptionPackages/toggleSubscriptionPackageStatus",
  async (uuid, { rejectWithValue }) => {
    try {
      const res = await http.patch(`/dashboard/subscription-packages/${uuid}/toggle-status`);
      return res;
    } catch (err) {
      return rejectWithValue(
        err.response?.data || "Failed to toggle subscription package status"
      );
    }
  }
);

const subscriptionPackagesSlice = createSlice({
  name: "subscriptionPackages",
  initialState: {
    subscriptionPackages: [],
    pagination: null,
    subscriptionPackage: null,
    packageCreateStatus: "idle",
    packageUpdateStatus: "idle",
    packageDeleteStatus: "idle",
    packageFetchStatus: "idle",
    packageStatusChangeStatus: "idle",
    loading: false,
    error: null,
  },
  reducers: {
    resetPackageStatuses: (state) => {
      state.packageCreateStatus = "idle";
      state.packageUpdateStatus = "idle";
      state.packageDeleteStatus = "idle";
      state.packageStatusChangeStatus = "idle";
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch subscription packages
      .addCase(fetchSubscriptionPackages.fulfilled, (state, action) => {
        state.subscriptionPackages = action.payload.data;
        state.pagination = action.payload.pagination;
      })

      // Create subscription package
      .addCase(createSubscriptionPackage.pending, (state) => {
        state.packageCreateStatus = "loading";
      })
      .addCase(createSubscriptionPackage.fulfilled, (state, action) => {
        state.packageCreateStatus = "success";
        state.subscriptionPackage = action.payload.data;
      })
      .addCase(createSubscriptionPackage.rejected, (state) => {
        state.packageCreateStatus = "failed";
      })

      // Fetch subscription package by ID
      .addCase(fetchSubscriptionPackageById.pending, (state) => {
        state.packageFetchStatus = "loading";
      })
      .addCase(fetchSubscriptionPackageById.fulfilled, (state, action) => {
        state.packageFetchStatus = "success";
        state.subscriptionPackage = action.payload.data;
      })
      .addCase(fetchSubscriptionPackageById.rejected, (state) => {
        state.packageFetchStatus = "failed";
        state.subscriptionPackage = null;
      })

      // Update subscription package
      .addCase(updateSubscriptionPackage.pending, (state) => {
        state.packageUpdateStatus = "loading";
      })
      .addCase(updateSubscriptionPackage.fulfilled, (state, action) => {
        state.packageUpdateStatus = "success";
        const index = state.subscriptionPackages.findIndex(
          (pkg) => pkg.uuid === action.meta.arg.uuid
        );
        if (index !== -1) {
          state.subscriptionPackages[index] = action.payload.data;
        }
        state.subscriptionPackage = action.payload.data;
      })
      .addCase(updateSubscriptionPackage.rejected, (state) => {
        state.packageUpdateStatus = "failed";
      })

      // Delete subscription package
      .addCase(deleteSubscriptionPackage.pending, (state) => {
        state.packageDeleteStatus = "loading";
      })
      .addCase(deleteSubscriptionPackage.fulfilled, (state, action) => {
        state.packageDeleteStatus = "success";
        state.subscriptionPackages = state.subscriptionPackages.filter(
          (pkg) => pkg.uuid !== action.payload.uuid
        );
      })
      .addCase(deleteSubscriptionPackage.rejected, (state) => {
        state.packageDeleteStatus = "failed";
      })

      // Toggle status
      .addCase(toggleSubscriptionPackageStatus.pending, (state) => {
        state.packageStatusChangeStatus = "loading";
      })
      .addCase(toggleSubscriptionPackageStatus.fulfilled, (state, action) => {
        state.packageStatusChangeStatus = "success";
        const index = state.subscriptionPackages.findIndex(
          (pkg) => pkg.uuid === action.meta.arg
        );
        if (index !== -1) {
          state.subscriptionPackages[index] = action.payload.data;
        }
      })
      .addCase(toggleSubscriptionPackageStatus.rejected, (state) => {
        state.packageStatusChangeStatus = "failed";
      })

      .addMatcher(isPending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addMatcher(isFulfilled, (state) => {
        state.loading = false;
      })
      .addMatcher(isRejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      });
  },
});

export const { resetPackageStatuses } = subscriptionPackagesSlice.actions;
export default subscriptionPackagesSlice.reducer;
